import { OrderingOperations, OrderingSelectors } from 'polygon-ordering';
import queryString from 'query-string';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import EVENTS from '../constants/events';
import { MENU_FILTERS_MODAL_ID } from '../modals/MenuFiltersModal';
import { CurrentCategoryContext } from '../screens/MenuScreen';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import getFilteredMenuItemCount from '../selectors/getFilteredMenuItemCount';
import getThemeLookup from '../selectors/getThemeLookup';
import { setCurrentModalId } from '../slices/currentModalId';
import { setMenuCategoriesBarHeight } from '../slices/menuCategoriesBarHeight';
import { logEvent } from '../utils/analytics';
import combineStyles from '../utils/combineStyles';
import debug from '../utils/debug';
import HorizontalScrollableMenu from './HorizontalScrollableMenu';
import MenuFilterButton from './MenuFilterButton';
import MenuSearchBar from './MenuSearchBar';
import MenuSearchButton from './MenuSearchButton';
import { SALE_TYPE } from '../libs/polygon-ordering/src/constants/saleType';

const { setCurrentMenuBrandId: setCurrentMenuBrandIdRedux } = OrderingOperations;
const {
  getDisableAllBrandsMenuTab,
  getLocationBrandsWithMenuRepresentation,
  getCurrentMenuBrandId,
  getFilteredRootCategory,
  getMenuSearchText,
  getSaleType,
  getDeliveryTime,
} = OrderingSelectors;

const MenuCategoriesBar: React.FC<{
  jumpToTop: () => void;
  setCurrentCategoryId: (value: string) => void;
}> = props => {
  const ref = useRef<HTMLDivElement>(null);
  const { jumpToTop, setCurrentCategoryId } = props;
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const p = useAppSelector(getThemeLookup);
  const rootCategory = useAppSelector(getFilteredRootCategory);
  const representedBrands = useAppSelector(getLocationBrandsWithMenuRepresentation);

  const currentCategoryId = useContext(CurrentCategoryContext);
  const brandId = useAppSelector(getCurrentMenuBrandId);
  const [currentMenuBrandId, setCurrentMenuBrandId] = useState<string | null>(brandId || '0');

  const itemCount = useAppSelector(getFilteredMenuItemCount);
  const menuCategoriesBarHeight = useAppSelector(state => state.menuCategoriesBarHeight);
  const disableAllBrandsMenuTab = useAppSelector(getDisableAllBrandsMenuTab);
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);

  const brandIdChangedRef = useRef(currentMenuBrandId);

  const saleType = useAppSelector(getSaleType);
  const desiredDeliveryTime = useAppSelector(state => state.desiredDeliveryTime);
  const deliveryTime = useAppSelector(getDeliveryTime);
  const selectedScheduledTime = saleType === SALE_TYPE.DELIVERY ? deliveryTime : null;
  const currentDay = selectedScheduledTime
    ? new Date(selectedScheduledTime as string)
        .toLocaleString('en-US', { weekday: 'long' })
        .toUpperCase()
    : null;
  const currentTime = selectedScheduledTime
    ? new Date(selectedScheduledTime as string).toTimeString().slice(0, 8)
    : null; // Get time in HH:MM:SS format

  const menuSearchText = useAppSelector(getMenuSearchText);
  const [searchActive, setSearchActive] = useState<boolean>(false);

  useEffect(() => {
    // open the search bar if there is an active search on mount
    // this search stuff should really be done with navigation instead of state variables
    if (menuSearchText !== null) {
      setSearchActive(true);
    }
  }, []);

  const toggleFilterActive = () => {
    dispatch(setCurrentModalId(MENU_FILTERS_MODAL_ID));
  };

  const toggleSearchActive = () => {
    logEvent(searchActive ? EVENTS.CLOSE_MENU_SEARCH : EVENTS.OPEN_MENU_SEARCH);
    setSearchActive(!searchActive);
  };
  const urlBrandId = queryString.parse(window.location.search);

  useEffect(() => {
    jumpToTop();
    try {
      const updatedHeight = ref.current!.offsetHeight;

      if (menuCategoriesBarHeight !== updatedHeight) {
        dispatch(setMenuCategoriesBarHeight(updatedHeight));
      }
    } catch (e) {
      debug(false, 'failed to set new menuCategoriesBarHeight');
    }
  }, [currentMenuBrandId]);

  useEffect(() => {
    if (brandIdChangedRef.current === currentMenuBrandId) {
      jumpToTop();
    }
    try {
      const updatedHeight = ref.current!.offsetHeight;

      if (menuCategoriesBarHeight !== updatedHeight) {
        dispatch(setMenuCategoriesBarHeight(updatedHeight));
      }
    } catch (e) {
      debug(false, 'failed to set new menuCategoriesBarHeight');
    }
  }, [itemCount]);

  useEffect(() => {
    if (urlBrandId && urlBrandId.brand_id) {
      setCurrentMenuBrandId(urlBrandId.brand_id as string);
      dispatch(setCurrentMenuBrandIdRedux(urlBrandId.brand_id as string));
    }
  }, [urlBrandId]);

  const categories = rootCategory?.subCategories || [];

  const brandTabs: Brand[] = [...representedBrands];

  if (!disableAllBrandsMenuTab) {
    //@ts-ignore
    brandTabs.unshift({ name: t('allBrandsLabel'), id: '0' }); //not a brand type
  }

  const categoryWithAvailability = categories.filter(cat => {
    if (selectedScheduledTime && currentDay && currentTime) {
      const isWithinDayCategory = cat.days.length
        ? cat.days.map(day => String(day)).includes(currentDay)
        : true;
      const isWithinTimeCategory =
        cat?.start && cat?.end ? currentTime >= cat?.start && currentTime <= cat?.end : true; // No time constraint if start or end is null
      return isWithinDayCategory && isWithinTimeCategory; // Filter out items not available now
    } else {
      return true;
    }
  });

  return (
    <div
      ref={ref}
      className="sticky"
      style={combineStyles(
        styles.outerContainer,
        p('menuCategoriesBar', ['boxShadow', 'borderTop', 'borderBottom']),
      )}
    >
      {representedBrands.length > 1 && (
        <div style={combineStyles(styles.rowContainer, p('menuBrandsBar', ['backgroundColor']))}>
          <HorizontalScrollableMenu
            containerStyle={{
              marginRight: '0.8em',
            }}
            items={brandTabs}
            currentItemId={currentMenuBrandId}
            setCurrentItemId={id => {
              setCurrentMenuBrandId(id);
              dispatch(setCurrentMenuBrandIdRedux(id));
              logEvent(EVENTS.CHANGE_MENU_BRAND_FILTER, { value: parseInt(id) });
            }}
            labelThemeKey={'menuBrandsBarLabel'}
            currentLabelThemeKey={'menuBrandsBarLabelCurrent'}
            currentIndicatorThemeKey={'menuBrandsBarCurrentIndicator'}
            iconSize={35}
            containerBackgroundColor={p('menuBrandsBar', ['backgroundColor']).backgroundColor}
            isBrand
            scrollContainerStyle={deviceTypeMobile ? styles.scrollContainerMobile : {}}
          />
        </div>
      )}

      <div
        style={combineStyles(
          styles.rowContainer,
          searchActive && styles.menuSearchContainer,
          p('menuCategoriesBar', ['backgroundColor']),
          searchActive && p('menuSearchBar', ['backgroundColor', 'boxShadow']),
        )}
      >
        {searchActive ? (
          <MenuSearchBar toggleSearchActive={toggleSearchActive} />
        ) : (
          <>
            <HorizontalScrollableMenu
              items={categoryWithAvailability}
              currentItemId={currentCategoryId}
              setCurrentItemId={value => {
                setCurrentCategoryId(value);
                logEvent(EVENTS.JUMP_TO_MENU_CATEGORY, { label: value });
              }}
              labelThemeKey={'categoryBarLabel'}
              currentLabelThemeKey={'categoryBarLabelCurrent'}
              currentIndicatorThemeKey={'categoryBarCurrentIndicator'}
              containerBackgroundColor={p('menuCategoriesBar', ['backgroundColor']).backgroundColor}
              scrollContainerStyle={
                deviceTypeMobile ? styles.scrollContainerMobile : styles.scrollContainer
              }
            />

            {!deviceTypeMobile && (
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <MenuSearchButton toggleSearchActive={toggleSearchActive} />
                <MenuFilterButton toggleFilterActive={toggleFilterActive} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles: Styles = {
  outerContainer: {
    zIndex: 3, // prevent the header bar box shadow from appearing on top of this
    top: 0, // kicks in during sticky mode
    width: '100%',
  },
  rowContainer: {
    borderTop: '1px solid rgba(0, 0, 0, 0.05)',
    //minHeight: 66,
    width: '100%',
    paddingBottom: 0,
    overflowX: 'hidden',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: '1em',
    // zIndex: 2, // prevent the header bar box shadow from appearing on top of this
  },
  scrollContainer: {
    padding: '18px 20px',
  },
  scrollContainerMobile: {
    padding: '10px 10px',
    minHeight: 45,
  },
  menuSearchContainer: {
    paddingTop: 15,
    paddingBottom: 10,
  },
};

export default MenuCategoriesBar;
